/**
 * Phone Number Types
 *
 * Branded types for phone numbers with specific format requirements.
 * E164PhoneNumber represents phone numbers in E.164 international format.
 *
 * E.164 format: +[country code][subscriber number]
 * - Starts with +
 * - Followed by 1-15 digits
 * - No spaces, dashes, or other formatting
 * - Examples: +14155551234, +447911123456
 *
 * NOTE: Branded types intentionally don't use the "Type" suffix as they
 * represent values with specific semantic meaning, not plain type aliases.
 *
 * @example
 * ```typescript
 * // Creating E164 phone numbers with validation
 * const phone = E164PhoneNumber.create('+14155551234'); // Valid
 *
 * // This will throw at runtime:
 * E164PhoneNumber.create('4155551234');  // Error: missing + prefix
 * E164PhoneNumber.create('+1-415-555-1234'); // Error: contains dashes
 *
 * // Type-safe usage - compiler knows the constraint
 * function sendSms(to: E164PhoneNumber, message: string): void {
 *   // to is guaranteed to be valid E.164 format
 * }
 * ```
 */

import { z } from "zod";
import { InvalidPhoneNumberError } from "../errors/invalid-phone-number.error.js";

export const E164PhoneNumberSchema = z
  .string()
  .refine((val) => isValidE164(val), {
    message: "Invalid E.164 phone number format",
  })
  .brand("E164PhoneNumber");

export type E164PhoneNumber = z.infer<typeof E164PhoneNumberSchema>;

/**
 * E.164 phone number regex pattern
 *
 * Matches: +[1-9][0-9]{6,14}
 * - Must start with +
 * - First digit after + must be 1-9 (no leading zeros in country codes)
 * - Followed by 6-14 additional digits (total 7-15 digits)
 *
 * Note: E.164 technically allows 1-15 digits, but we enforce a minimum of 7 as a
 * pragmatic constraint to prevent obviously invalid inputs like "+1" or "+99".
 * This may reject some valid short codes or special service numbers in rare territories,
 * but provides better protection against typos and malformed data for our use case.
 */
const E164_REGEX = /^\+[1-9]\d{6,14}$/;

/**
 * Normalize country-specific phone number variants to canonical short form.
 *
 * Argentina, Brazil, and Mexico have phone numbers where a mobile prefix
 * produces an alternate E.164 representation that reaches the same phone.
 * We strip the mobile prefix to canonicalize to the short form because:
 *
 * - The long form (with prefix) unambiguously identifies a mobile number,
 *   so stripping the prefix is always safe — both forms reach the same phone.
 * - The short form could be mobile or landline. Adding a mobile prefix to a
 *   landline would mangle it, so adding is NOT safe.
 *
 * @see https://en.wikipedia.org/wiki/Telephone_numbers_in_Argentina
 * @see https://en.wikipedia.org/wiki/Telephone_numbers_in_Brazil
 * @see https://support.bandwidth.com/hc/en-us/articles/360051395973-Mexico-dial-plan-changes
 * @see https://faq.whatsapp.com/1294841057948784
 */
function normalizeCountryVariants(cleaned: string): string {
  cleaned = cleaned.replace(/^\+549(\d{10})$/, "+54$1");

  cleaned = cleaned.replace(/^\+55(\d{2})9([6-9]\d{7})$/, "+55$1$2");

  cleaned = cleaned.replace(/^\+521(\d{10})$/, "+52$1");

  return cleaned;
}

function normalizePhone(value: string): string {
  let cleaned = value.replace(/[\s\-().]/g, "");

  if (!cleaned.startsWith("+") && /^[1-9]\d{9,14}$/.test(cleaned)) {
    cleaned = `+${cleaned}`;
  }

  return normalizeCountryVariants(cleaned);
}

function isValidE164(value: string): boolean {
  return typeof value === "string" && E164_REGEX.test(value);
}

export const E164PhoneNumber = {
  /**
   * Create an E164PhoneNumber from a string, with normalization and validation.
   * @param value - String to convert (can have formatting like spaces/dashes)
   * @returns E164PhoneNumber branded value
   * @throws InvalidPhoneNumberError if value cannot be normalized to valid E.164 format
   * @example
   * E164PhoneNumber.create('+14155551234')    // Returns '+14155551234'
   * E164PhoneNumber.create('+1 415 555 1234') // Returns '+14155551234'
   * E164PhoneNumber.create('14155551234')     // Returns '+14155551234'
   * E164PhoneNumber.create('+1-415-555-1234') // Returns '+14155551234'
   */
  create(value: string): E164PhoneNumber {
    const normalized = normalizePhone(value);
    if (!isValidE164(normalized)) {
      throw new InvalidPhoneNumberError(value);
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as E164PhoneNumber;
  },

  unsafeCreate(value: string): E164PhoneNumber {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as E164PhoneNumber;
  },

  isValid(value: string): boolean {
    return isValidE164(value);
  },

  isE164PhoneNumber(value: string): value is E164PhoneNumber {
    return isValidE164(value);
  },

  tryCreate(value: string): E164PhoneNumber | undefined {
    const normalized = normalizePhone(value);
    if (!isValidE164(normalized)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as E164PhoneNumber;
  },

  toDigitsOnly(value: string): string {
    return value.replace(/^\+/, "").replace(/\s/g, "");
  },
} as const;
